import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, FileClock, Lock, RefreshCw, RotateCcw, Send, Undo2, Wand2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg } from "./kpiFormat";

const STEPS = {
  submit: { label: "Submit for review", icon: Send, title: "Send this draft for review?" },
  return: { label: "Return to draft", icon: Undo2, title: "Return this report to draft?" },
  approve: { label: "Approve", icon: CheckCircle2, title: "Approve these figures?", owner: true },
  reopen: { label: "Reopen review", icon: RotateCcw, title: "Reopen this report for review?", owner: true },
  lock: { label: "Lock", icon: Lock, title: "Lock this week? Changes after this need a versioned correction.", owner: true },
};
const BY_STATUS = { draft: ["submit"], review: ["return", "approve"], approved: ["lock", "reopen"], locked: [] };

const StepDialog = ({ open, step, busy, onClose, onConfirm, correction }) => {
  const [note, setNote] = useState("");
  const meta = correction ? { title: "Request a correction (restatement)" } : STEPS[step] || {};
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="kpi-step-dialog">
        <DialogHeader>
          <DialogTitle>{meta.title}</DialogTitle>
          <DialogDescription>
            {correction
              ? "The approved version is kept in the history. A new draft version is calculated with today's data and settings."
              : "Add an optional note for the audit trail."}
          </DialogDescription>
        </DialogHeader>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="kpi-step-note"
          placeholder={correction ? "What needs correcting? (required)" : "Note (optional)"} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="kpi-step-cancel">Cancel</Button>
          <Button disabled={busy || (correction && note.trim().length < 5)} onClick={() => onConfirm(note)}
            data-testid="kpi-step-confirm">Confirm</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const WorkflowActions = ({ report, canApprove, onChange }) => {
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const wk = report.week_start;

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const r = await fn();
      onChange(r.data);
      toast.success(ok);
      setDialog(null);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const generate = () => run(() => api.post(`/kpi/reports/${wk}/generate`, null, { timeout: 120000 }),
    report.status === "none" ? "Draft generated" : "Draft refreshed");
  const confirm = (note) => dialog === "correction"
    ? run(() => api.post(`/kpi/reports/${wk}/correction`, { reason: note }), "Correction draft created")
    : run(() => api.post(`/kpi/reports/${wk}/transition`, { action: dialog, note }), "Report updated");

  const steps = (BY_STATUS[report.status] || []).filter((s) => !STEPS[s].owner || canApprove);
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="kpi-workflow-actions">
      {(report.status === "none" || report.status === "draft") && (
        <Button size="sm" variant={report.status === "none" ? "default" : "outline"} disabled={busy || !report.finished}
          onClick={generate} data-testid="kpi-generate-btn"
          title={report.finished ? undefined : "This week hasn't finished yet"}>
          {report.status === "none" ? <Wand2 className="h-4 w-4 mr-1.5" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
          {report.status === "none" ? "Generate draft" : "Refresh draft"}
        </Button>
      )}
      {steps.map((s) => {
        const Icon = STEPS[s].icon;
        return (
          <Button key={s} size="sm" variant={s === "approve" || s === "submit" ? "default" : "outline"} disabled={busy}
            onClick={() => setDialog(s)} data-testid={`kpi-step-${s}-btn`}>
            <Icon className="h-4 w-4 mr-1.5" />{STEPS[s].label}
          </Button>
        );
      })}
      {canApprove && ["approved", "locked"].includes(report.status) && (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => setDialog("correction")} data-testid="kpi-correction-btn">
          <FileClock className="h-4 w-4 mr-1.5" />Request correction
        </Button>
      )}
      {dialog && (
        <StepDialog key={dialog} open step={dialog} correction={dialog === "correction"} busy={busy}
          onClose={() => setDialog(null)} onConfirm={confirm} />
      )}
    </div>
  );
};
